import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "@/lib/i18n";
import { CATEGORIES, formatKm } from "@/lib/categories";
import { fmtHours } from "@/lib/i18n";
import { toSpeech, stopAllAudio } from "@/components/ListenButton";
import { CANCEL, MIC, PAUSED, beep, detectVoice, fetchTTS, overlap, parseCommand, recordUtterance, splitSteps, transcribe } from "@/lib/voiceGuide";

export const useVoiceGuide = ({ chat, offices, geo, casesApi, openChat }) => {
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [paused, setPaused] = useState(false);
  const [caption, setCaption] = useState("");
  const token = useRef(0);
  const audio = useRef(null);
  const skipRef = useRef(null);
  const abortRec = useRef(null);
  const stream = useRef(null);
  const pausedRef = useRef(false);
  const resumeRef = useRef(null);
  const ctxRef = useRef({});
  const lastQA = useRef(null);
  const running = useRef(null);
  const skipPending = useRef(false);
  const bargeAbort = useRef(null);
  const gestureRef = useRef(false);
  const [needsGesture, setNeedsGesture] = useState(false);
  ctxRef.current = { chat, offices, geo, casesApi, openChat };

  const guard = (tk) => { if (tk !== token.current) throw CANCEL; };
  const waitResume = (tk) => (pausedRef.current ? new Promise((r) => (resumeRef.current = r)).then(() => guard(tk)) : guard(tk));

  const waitGesture = () =>
    new Promise((r) => {
      const evs = ["pointerdown", "keydown", "touchstart"];
      const h = () => { evs.forEach((ev) => window.removeEventListener(ev, h, true)); setTimeout(() => { gestureRef.current = false; setNeedsGesture(false); }, 0); r(); };
      gestureRef.current = true;
      setNeedsGesture(true);
      evs.forEach((ev) => window.addEventListener(ev, h, true));
    });

  const play = (a) =>
    a.play().catch(async (e) => {
      if (e?.name !== "NotAllowedError") return skipRef.current?.();
      await waitGesture();
      if (audio.current === a && !pausedRef.current) a.play().catch(() => skipRef.current?.());
    });

  // Plays Clara's voice; returns what the user said if they interrupted her, else null.
  const speak = async (tk, text, { barge = true } = {}) => {
    await waitResume(tk);
    const clean = toSpeech(text);
    if (!clean) return null;
    setPhase("speaking");
    setCaption(clean);
    const url = await fetchTTS(clean).catch(() => null);
    guard(tk);
    if (!url) return null;
    if (skipPending.current) { skipPending.current = false; return null; }
    const a = new Audio(url);
    audio.current = a;
    const ended = new Promise((resolve) => { skipRef.current = resolve; a.onended = resolve; a.onerror = resolve; });
    if (!pausedRef.current) play(a);
    let said = null;
    while (barge && stream.current && !said) {
      const r = await Promise.race([ended.then(() => "end"), detectVoice(stream.current, bargeAbort)]);
      bargeAbort.current?.();
      guard(tk);
      if (r !== true) break;
      a.pause();
      setPhase("listening");
      setCaption(t("guide.listeningLbl"));
      const blob = await recordUtterance(stream.current, abortRec, { startSpoken: true, silenceMs: 1200 });
      guard(tk);
      const txt = blob && blob !== PAUSED ? (await transcribe(blob).catch(() => "")).trim() : "";
      guard(tk);
      if (txt && overlap(txt, clean) < 0.5) { said = txt; setCaption(`“${txt}”`); break; }
      setPhase("speaking");
      setCaption(clean);
      if (!pausedRef.current) a.play().catch(() => skipRef.current?.());
    }
    if (!said) await ended;
    else a.pause();
    audio.current = null;
    skipRef.current = null;
    guard(tk);
    return said;
  };

  const listen = async (tk) => {
    for (;;) {
      await waitResume(tk);
      if (!stream.current) stream.current = await navigator.mediaDevices.getUserMedia(MIC);
      guard(tk);
      setPhase("listening");
      setCaption(t("guide.listeningLbl"));
      beep(880);
      const blob = await recordUtterance(stream.current, abortRec);
      guard(tk);
      if (blob === PAUSED) continue;
      beep(560, 120);
      if (!blob) return "";
      setPhase("thinking");
      const text = (await transcribe(blob).catch(() => "")).trim();
      guard(tk);
      if (text) setCaption(`“${text}”`);
      return text;
    }
  };

  const nearbyText = () => {
    const list = ctxRef.current.offices.slice(0, 3).map((o) =>
      t("guide.officeItem", { name: o.name, cat: CATEGORIES[o.category]?.label || "", dist: formatKm(o.distance_km), hours: fmtHours(o.hours) })
    );
    return list.length ? t("guide.nearby", { list: list.join(". ") }) : t("dir.emptyT");
  };

  // Reads the answer section by section as it streams in; the user can interrupt at any moment.
  const readSteps = async (tk, getStep) => {
    let said = await speak(tk, t("guide.stepIntro"));
    let i = 0;
    for (;;) {
      if (!said) {
        const step = await getStep(i);
        guard(tk);
        if (step == null) break;
        said = await speak(tk, step);
        if (!said) { i += 1; continue; }
      }
      const cmd = parseCommand(said);
      const text = said;
      said = null;
      if (cmd === "repeat") continue;
      if (cmd === "next") { i += 1; continue; }
      if (cmd === "pause") { togglePause(); continue; }
      if (cmd === "exit") return { exit: true };
      return { question: text };
    }
    const after = await speak(tk, t("guide.done"));
    return after ? { question: after } : {};
  };

  const streamSteps = (tk, send) => {
    const steps = [];
    let done = false;
    let waiters = [];
    const notify = () => { waiters.forEach((w) => w()); waiters = []; };
    const push = (parts) => { while (steps.length < parts.length) steps.push(parts[steps.length]); notify(); };
    const answerP = send((full) => push(splitSteps(full).slice(0, -1))).then((ans) => {
      if (ans) push(splitSteps(ans));
      done = true;
      notify();
      return ans;
    });
    const getStep = async (i) => {
      while (i >= steps.length && !done) { await new Promise((r) => waiters.push(r)); guard(tk); }
      return steps[i] ?? null;
    };
    return { getStep, answerP };
  };

  const followUp = async (tk) => {
    if (!lastQA.current) return speak(tk, t("guide.noCase"));
    for (let tries = 0; tries < 2; tries++) {
      await speak(tk, t("guide.askPhone"));
      let digits = (await listen(tk)).replace(/\D/g, "");
      if (digits.length === 12 && digits.startsWith("52")) digits = digits.slice(2);
      if (digits.length === 10) {
        const c = await ctxRef.current.casesApi.create({ ...lastQA.current, phone: digits }).catch(() => null);
        guard(tk);
        if (!c) break;
        const spelled = c.folio.split("").join(" ");
        return speak(tk, t("guide.caseDone", { folio: spelled }));
      }
      if (tries === 0) await speak(tk, t("guide.badPhone"));
    }
    return speak(tk, t("fu.err"));
  };

  const converse = async (tk, initial = null) => {
    let misses = 0;
    let pending = initial;
    for (;;) {
      const said = pending ?? (await listen(tk));
      pending = null;
      if (!said) {
        misses += 1;
        if (misses >= 2) { await speak(tk, t("guide.idle")); setPhase("idle"); return; }
        pending = await speak(tk, t("guide.noHear"));
        continue;
      }
      misses = 0;
      const cmd = parseCommand(said);
      if (cmd === "exit") return stop(true);
      if (cmd === "pause") { togglePause(); continue; }
      const sayAndGo = async (text) => { pending = await speak(tk, text); };
      if (cmd === "help") { await sayAndGo(t("guide.howto")); continue; }
      if (cmd === "nearby") { await sayAndGo(nearbyText()); continue; }
      if (cmd === "followup") { await followUp(tk); continue; }
      if (cmd === "repeat" && lastQA.current) {
        const all = splitSteps(lastQA.current.answer);
        const r = await readSteps(tk, async (i) => all[i] ?? null);
        if (r.exit) return stop(true);
        pending = r.question || null;
        continue;
      }
      const { chat: c, geo: g, openChat: oc } = ctxRef.current;
      oc();
      setPhase("thinking");
      setCaption(t("guide.thinkingLbl"));
      const { getStep, answerP } = streamSteps(tk, (onDelta) => c.send(said, { lat: g.position[0], lng: g.position[1], label: g.place }, onDelta));
      const first = await getStep(0);
      guard(tk);
      if (first == null) { await speak(tk, t("ai.err")); continue; }
      answerP.then((answer) => { if (answer) lastQA.current = { question: said, answer, location_label: g.place }; });
      const r = await readSteps(tk, getStep);
      if (r.exit) return stop(true);
      pending = r.question || null;
    }
  };

  const loop = async (tk, intro) => {
    if (running.current === tk) return;
    running.current = tk;
    try {
      let first = null;
      if (intro) {
        navigator.mediaDevices?.getUserMedia(MIC).then((s) => {
          if (tk !== token.current) return s.getTracks().forEach((tr) => tr.stop());
          stream.current ||= s;
        }).catch(() => {});
        const { offices: o, geo: g } = ctxRef.current;
        const n = o[0];
        first = await speak(tk, t("guide.welcome", { n: o.length, place: g.place, nearest: n ? n.name : "—", dist: n ? formatKm(n.distance_km) : "" }));
        first ??= await speak(tk, t("guide.howto"));
      }
      await converse(tk, first);
    } catch (e) {
      if (e !== CANCEL) { console.error(e); setPhase("idle"); setCaption(t("voice.denied")); }
    } finally {
      if (running.current === tk) running.current = null;
    }
  };

  const start = () => {
    stopAllAudio();
    localStorage.setItem("ac-voice-auto", "on");
    token.current += 1;
    pausedRef.current = false;
    setPaused(false);
    skipPending.current = false;
    setActive(true);
    loop(token.current, true);
  };

  const stop = useCallback((announce) => {
    token.current += 1;
    abortRec.current?.();
    bargeAbort.current?.();
    audio.current?.pause();
    skipRef.current?.();
    resumeRef.current?.();
    stream.current?.getTracks().forEach((tr) => tr.stop());
    stream.current = null;
    setActive(false);
    setPhase("idle");
    setPaused(false);
    pausedRef.current = false;
    setCaption("");
    if (announce === true) {
      localStorage.setItem("ac-voice-auto", "off");
      fetchTTS(t("guide.bye")).then((u) => new Audio(u).play()).catch(() => {});
    }
  }, []);

  const togglePause = () => {
    if (!pausedRef.current) {
      pausedRef.current = true;
      setPaused(true);
      audio.current?.pause();
      abortRec.current?.();
      bargeAbort.current?.();
    } else {
      pausedRef.current = false;
      setPaused(false);
      audio.current?.play().catch(() => skipRef.current?.());
      resumeRef.current?.();
      resumeRef.current = null;
    }
  };

  const skip = () => {
    if (skipRef.current) { audio.current?.pause(); skipRef.current(); }
    else skipPending.current = true;
  };

  const talk = () => {
    if (pausedRef.current) togglePause();
    if (phase === "speaking") return skip();
    if (running.current !== token.current) loop(token.current, false);
  };

  const ref = useRef({});
  ref.current = { active, start, talk, togglePause };
  useEffect(() => {
    const onKey = (e) => {
      const r = ref.current;
      if (gestureRef.current) return;
      if (e.key === "Escape" && r.active) { e.preventDefault(); r.togglePause(); return; }
      const tag = (e.target.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target.isContentEditable) return;
      if (e.key.toLowerCase() === "v" && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); r.active ? r.talk() : r.start(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => () => stop(), [stop]);

  return { active, phase, paused, caption, needsGesture, start, stop, togglePause, skip, talk };
};
